import { debounce } from "lodash-es";
import { nanoid } from "nanoid";
import { createContext, createRef, type FC, useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router";
import { CardGrid, CardGridArea } from "widgets";

import { type BlastJob, type JobResult } from "@/graphql.ts";
import { toggleColoringMode } from "@/redux/modules/blast.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { BLAST_METHOD, BLAST_SUB_VIEW, VIEW } from "@/settings/settings.ts";

import ButtonHeader from "../core/ButtonHeader.js";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { Checkbox } from "../core/forms/Checkbox.js";
import ItemPagination from "../core/navigation-panel/ItemPagination.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import { default as AlnPaginationModel, type IPaginationMap } from "./AlnPaginationModel.js";
import BlastPageHeader from "./BlastPageHeader.js";
import { SensorAlnInfoCard, SequenceType } from "./SensorAlnInfoCard.js";

interface IBlastResultAlignmentsViewProps {
  job: BlastJob;
  queryId: number;
  isLoading: boolean;
  jobResult?: JobResult;

  isUiSmall?: boolean; // from PageHeader
}

/**
 * This context is used to pass the scroll behavior to the deepest children.
 * ref: This is the scrollable element
 * scrollUpdate: This state updates on each debounced scroll ror window.resize to force the drawing of the alignment.
 */
const BlastResultContext = createContext({ ref: createRef<HTMLDivElement>(), scrollUpdate: nanoid() });

const BlastResultAlignmentsView: FC<IBlastResultAlignmentsViewProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const hitNum = useMatch("/job/:jobId/results/:queryId/alignments/:hitNum/*")?.params.hitNum;
  const hspNum = useMatch("/job/:jobId/results/:queryId/alignments/:hitNum/:hspNum")?.params.hspNum;

  // Scrollable wrapper element
  const ref = useRef<HTMLDivElement>(null);

  // Color mismatch checkbox
  const colorMismatchesOnly = useSelector<IAppState, boolean>((state) => state.blast.colorMismatchesOnly, shallowEqual);

  // Pagination management
  const [pagination, setPagination] = useState<IPaginationMap>();
  const [page, setPage] = useState<number>();

  useEffect(() => {
    if (!props.isLoading && props.jobResult?.blastHits) {
      const model = new AlnPaginationModel(props.jobResult?.blastHits);
      setPagination(model);
    }
  }, [props.jobResult, props.isLoading]);

  useEffect(() => {
    if (pagination) {
      setPage(pagination.getPageOf(Number(hitNum), hspNum ? Number(hspNum) : undefined));
    }
  }, [hitNum, hspNum, pagination]);

  const handlePageChange = useCallback(
    (idx: number) => {
      if (page !== idx && pagination && page != undefined) {
        // the URL is the single point of control (so that state and URL are in sync, necessary for saved views and links)
        const hits = pagination.getHitsForPage(idx);
        const firstHitIndex = hits?.length && hits[0].rank!;
        const firstHspNum = firstHitIndex && pagination.getFirstHspForPage(idx, firstHitIndex);
        navigate(
          `/job/${props.job.id}/results/${props.queryId}/alignments/${firstHitIndex}/${
            firstHspNum > 1 ? firstHspNum : ""
          }`,
        );
      }
    },
    [page, pagination, props.job.id, props.queryId],
  );

  // Scroll / resize management
  const [scrollUpdate, setScrollUpdate] = useState(nanoid());

  useEffect(() => {
    const element = ref.current;
    const fn = debounce(() => setScrollUpdate(nanoid()), 100, { trailing: true });
    element?.addEventListener("scroll", fn);
    window.addEventListener("resize", fn);

    // clean events
    return () => {
      element?.removeEventListener("scroll", fn);
      window.removeEventListener("resize", fn);
    };
  }, []);

  return (
    <BlastResultContext.Provider value={{ ref, scrollUpdate }}>
      <BlastPageHeader
        job={props.job}
        jobResult={props.jobResult}
        subtitle={"Alignments"}
        toolsWidgets={[
          <ItemPagination
            items={Array.from(Array(pagination?.getNumberOfPages()), (_x, i) => i + 1)}
            selectedItem={page}
            onPageChange={({ currentIndex }) => handlePageChange(currentIndex + 1)}
            hasKeyboard={false}
            isUiSmall={props.isUiSmall}
            key={"blast-result-alignments-view-1"}
          />,
          <div className="checkbox check-default" key={"blast-result-alignments-view-action-2"}>
            <Checkbox
              id="mismatchColoringCheckbox"
              label={props.isUiSmall ? "Mismatch" : "Color Mismatches Only"}
              checked={colorMismatchesOnly}
              onChange={() => dispatch(toggleColoringMode())}
            />
          </div>,
        ]}
        actionWidgets={[
          <ButtonHeader
            viewType={VIEW.BLAST_JOB_VIEW.type}
            subViewType={BLAST_SUB_VIEW.ALIGNMENTS}
            hideBlastBtn={true}
            hideExportBtn={true}
            key={"blast-result-alignments-view-action-3"}
          />,
        ]}
        isLoading={props.isLoading}
      />
      <CardGrid ref={ref} isLoading={props.isLoading} className={styleLayout.page}>
        {!props.jobResult?.blastHits && <ContentPlaceholder>No hits found</ContentPlaceholder>}
        {pagination?.getHitsForPage(page ?? 1)?.map((hit) => (
          <SensorAlnInfoCard
            type={getResultSequenceType(props.job.params?.blast?.blastType?.toLowerCase())}
            hit={hit}
            jobId={props.job.id}
            queryId={props.queryId}
            area={CardGridArea.Header}
            key={`aln-info-card-${hit.rank}`}
          />
        ))}
      </CardGrid>
    </BlastResultContext.Provider>
  );
};

const getResultSequenceType = (type?: string): SequenceType => {
  switch (type) {
    case BLAST_METHOD.BLASTN:
      return SequenceType.DNA;
    case BLAST_METHOD.BLASTX:
    case BLAST_METHOD.BLASTP:
    case BLAST_METHOD.TBLASTN:
      return SequenceType.AMINOACID;
    default:
      throw new Error("Found unexpected BLAST type: " + type);
  }
};

export { BlastResultAlignmentsView, BlastResultContext };
